import { clsx } from 'clsx'
import { type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { productAccessorGetLowestPrice } from '../../../model/productAccessor/productAccessorGetLowestPrice'
import { ProductFormattedUnitPrice } from '../../ProductFormattedUnitPrice'
import { useProductPageContext } from '../../productPageContext'
import { ProductPriceOriginal } from '../../ProductPrice/ProductPriceOriginal'
import { useProductPrices } from '../hooks/useProductPrices'
import { bonusEnabledPrescriptionTypes } from '../models/bonusEnabledPrescriptionTypes'
import { ProductDiscountBadge } from '../ProductDiscountBadge'
import { ProductPriceTransparencyIcon } from '../ProductPriceTransparencyIcon'

import { ProductBuyBoxPriceLabel } from './ProductBuyBoxPriceLabel'
import { RetailPrice } from './RetailPrice'

const NoLongerVariant = (
  <div className="border-b border-b-dark-primary-min py-5 text-xl text-dark-primary-strong">
    <FormattedMessage id="productVariants.label.noLongerVariant" />
  </div>
)

const OutOfStock = (
  <div className="border-b border-b-dark-primary-min py-5 text-xl text-dark-primary-strong">
    <FormattedMessage id="general.label.outOfStock" />
  </div>
)

export const ProductVariantHeader: FC = () => {
  const { currentPrescriptionType, isMasterPage, offerId, product } =
    useProductPageContext()
  const {
    discount,
    displayPrice,
    formattedUnitPrice,
    isDiscountValid,
    isProductNotInCatalog,
    isProductOutOfSale,
    isProductOutOfStock,
    showThirtyDaysPrice,
  } = useProductPrices({ currentPrescriptionType, offerId, product })
  if (isProductNotInCatalog || isProductOutOfSale) {
    return NoLongerVariant
  }
  if (isProductOutOfStock && !isMasterPage) {
    return OutOfStock
  }

  return (
    <div className="flex flex-col border-b border-b-dark-primary-min pb-2">
      <div className="flex flex-col items-start">
        <div className="flex items-center justify-start gap-1">
          <ProductBuyBoxPriceLabel />
          <ProductPriceTransparencyIcon />
        </div>
        <div
          className={clsx(
            'mb-1.5 flex items-center',
            bonusEnabledPrescriptionTypes.includes(currentPrescriptionType)
              ? 'flex-row-reverse gap-2'
              : 'flex-row gap-1',
          )}
        >
          {!isMasterPage && (
            <ProductDiscountBadge
              currentPrescriptionType={currentPrescriptionType}
              data-qa-id="product-discount-badge"
              discount={discount}
              isDiscountValid={isDiscountValid}
              offerPrice={displayPrice}
              showInfo={false}
              showThirtyDaysPrice={showThirtyDaysPrice}
            />
          )}
          {(displayPrice ?? isMasterPage) ? (
            <RetailPrice
              currentPrescriptionType={currentPrescriptionType}
              data-qa-id="product-price"
              isDiscountValid={isDiscountValid}
              isMasterPage={isMasterPage}
              lowestPrice={productAccessorGetLowestPrice(product.variants)}
              offerPrice={displayPrice}
            />
          ) : null}
        </div>
      </div>
      {formattedUnitPrice && !isMasterPage ? (
        <ProductFormattedUnitPrice
          classNames="text-s"
          formattedUnitPrice={formattedUnitPrice}
        />
      ) : null}
      <ProductPriceOriginal
        className="text-dark-primary-strong"
        currentPrescriptionType={currentPrescriptionType}
        offerId={offerId}
        product={product}
      />
    </div>
  )
}
